import { useQuery } from "@tanstack/react-query";

import { AZURE_MJ } from "@/lib/axios";

export const CHIME_QUERY_KEYS = {
  getSiteAttendance: "CHIME_GET_SITE_ATTENDANCE",
  getActiveWorkers: "CHIME_GET_ACTIVE_WORKERS",
  getWorkersOnBreak: "CHIME_GET_WORKERS_ON_BREAK",
  getDepartedWorkers: "CHIME_GET_DEPARTED_WORKERS",
  getLabourCostSummary: "CHIME_GET_LABOUR_COST_SUMMARY",
  getDailyLabourCosts: "CHIME_GET_DAILY_LABOUR_COSTS",
  getLabourCostsByWorker: "CHIME_GET_LABOUR_COSTS_BY_WORKER",
  getLabourCostsByTrade: "CHIME_GET_LABOUR_COSTS_BY_TRADE",
  getWorkers: "CHIME_GET_WORKERS",
  getSites: "CHIME_GET_SITES",
  getTrades: "CHIME_GET_TRADES",
  getBusinessUnits: "CHIME_GET_BUSINESS_UNITS"
};

export type IAttendanceStatus = "Active" | "Break" | "Departed";

export interface ISiteAttendanceApiRes {
  totalToday: number;
  activeCount: number;
  onBreakCount: number;
  departedCount: number;
  workers: IAttendanceWorker[];
}

export interface IAttendanceWorker {
  resourceId: string;
  fullName: string;
  role: string;
  trade: string;
  status: IAttendanceStatus;
  checkInTime: string;
  expectedDeparture: string;
  actualDeparture: string;
  isAutoClockOut: boolean;
}

export interface IWorker {
  id: string;
  fullName: string;
  displayName: string;
  role: string;
  trade: string;
  tradeId: string;
  hourlyRate: number;
  dailyRate: number;
  businessUnit: string;
  isActive: boolean;
}

export interface ILabourCost {}

export interface ISite {
  id: string;
  name: string;
  reference: string | null;
  displayName: string | null;
  clientName: string | null;
  businessUnit: string | null;
  openingTime: string | null;
  closingTime: string | null;
  isLive: boolean;
}

export interface ITrade {}

export interface IBuisinessUnit {}

export const useGetSiteAttendance = (
  siteId: string | null,
  date: string | null
) => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getSiteAttendance, siteId, date],
    queryFn: async (): Promise<ISiteAttendanceApiRes> => {
      const { data } = await AZURE_MJ.get(
        `/chime/sites/${siteId}/attendance?date=${date || ""}`
      );

      return data;
    },
    enabled: !!siteId
  });
};

export const useGetActiveWorkers = (siteId?: string) => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getActiveWorkers, siteId],
    queryFn: async () => {
      const { data } = await AZURE_MJ.get(
        `/chime/sites/${siteId}/attendance/active`
      );

      return data;
    },
    enabled: !!siteId
  });
};

export const useGetWorkersOnBreak = (siteId?: string) => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getWorkersOnBreak, siteId],
    queryFn: async () => {
      const { data } = await AZURE_MJ.get(
        `/chime/sites/${siteId}/attendance/onbreak`
      );

      return data;
    },
    enabled: !!siteId
  });
};

export const useGetDepartedWorkers = (siteId?: string) => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getDepartedWorkers, siteId],
    queryFn: async () => {
      const { data } = await AZURE_MJ.get(
        `/chime/sites/${siteId}/attendance/departed`
      );

      return data;
    },
    enabled: !!siteId
  });
};

export const useGetLabourCostSummary = (
  siteId?: string,
  filters?: {
    from?: string;
    to?: string;
  }
) => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getLabourCostSummary, siteId, filters],
    queryFn: async () => {
      const searchParams = new URLSearchParams(
        Object.entries(filters ?? {}).filter(
          ([, value]) => value !== "" || value !== undefined
        )
      );

      const url = `/chime/sites/${siteId}/labour-costs${
        searchParams.toString() ? `?${searchParams}` : ""
      }`;

      const { data } = await AZURE_MJ.get(url);

      return data;
    },
    enabled: !!siteId
  });
};

export const useGetDailyLabourCosts = (siteId?: string) => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getDailyLabourCosts, siteId],
    queryFn: async () => {
      const { data } = await AZURE_MJ.get(
        `/chime/sites/${siteId}/labour-costs/daily`
      );

      return data;
    },
    enabled: !!siteId
  });
};

export const useGetLabourCostsByWorker = (siteId?: string) => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getLabourCostsByWorker, siteId],
    queryFn: async () => {
      const { data } = await AZURE_MJ.get(
        `/chime/sites/${siteId}/labour-costs/by-worker`
      );

      return data;
    },
    enabled: !!siteId
  });
};

export const useGetLabourCostsByTrade = (siteId?: string) => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getLabourCostsByTrade, siteId],
    queryFn: async () => {
      const { data } = await AZURE_MJ.get(
        `/chime/sites/${siteId}/labour-costs/by-trade`
      );

      return data;
    },
    enabled: !!siteId
  });
};

export const useGetWorkers = (businessUnitId?: string) => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getWorkers, businessUnitId],
    queryFn: async (): Promise<IWorker[]> => {
      const { data } = await AZURE_MJ.get(`/chime/people`, {
        params: { businessUnitId }
      });

      return Array.from(
        new Map(
          data
            .filter((item: IWorker) => item.trade !== "No Trade Set")
            .map((item: IWorker) => [item.id, item])
        ).values()
      ) as IWorker[];
    },
    enabled: !!businessUnitId
  });
};

export const useGetSites = () => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getSites],
    queryFn: async (): Promise<ISite[]> => {
      const { data } = await AZURE_MJ.get(`/chime/sites`);

      return data;
    }
  });
};

export const useGetTrades = () => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getTrades],
    queryFn: async () => {
      const { data } = await AZURE_MJ.get(`/chime/trades`);

      return data;
    }
  });
};

export const useGetBusinessUnits = () => {
  return useQuery({
    queryKey: [CHIME_QUERY_KEYS.getBusinessUnits],
    queryFn: async () => {
      const { data } = await AZURE_MJ.get(`/chime/business-units`);

      return data;
    }
  });
};
